
/* ============================== src/utils/csvExport.js ============================== */
// src/utils/csvExport.js
//
// Единая точка экспорта CSV. Раньше было две реализации
// (Cabinet.downloadCSV и Journal.exportToCSV) с разным поведением:
// разные разделители, разная обработка кавычек, разное экранирование,
// отсутствие BOM в одном месте и присутствие в другом. Это приводило к
// тому, что один и тот же отчёт в Excel открывался по-разному.
//
// Правила собраны здесь:
//   1. Все ячейки оборачиваются в кавычки - даже «безопасные». Это
//      снимает вопрос «а если тут встретится точка с запятой».
//   2. Кавычки внутри ячейки удваиваются по RFC 4180.
//   3. Ячейка, начинающаяся с =, +, -, @, \t или \r, префиксуется
//      одиночной кавычкой - защита от CSV-инъекций (OWASP).
//   4. BOM в начале файла - чтобы Excel не ломал кириллицу.
//   5. Разделитель строк - \r\n, а не \n: старые версии Excel на
//      Windows корректно читают только CRLF.
//   6. Имя файла санитизируется: `/`, `\`, `:`, `*`, `?`, `"`, `<`, `>`,
//      `|` запрещены на одной из поддерживаемых платформ и ломают
//      скачивание (Safari в частности отказывается открывать URL с
//      такими именами).
//   7. Ссылка на blob живёт до 500 мс после клика - Safari иначе
//      может прервать загрузку.
//   8. Тэг <a> добавляется в body на время клика - Firefox без этого
//      не всегда запускает скачивание.

/**
 * Ячейка начинается с символа, который Excel/LibreOffice трактует как
 * начало формулы. Даже `-` опасен: `-1+1` в Excel станет числом, а в
 * связке с последующими символами - может быть частью формулы.
 */
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

/**
 * Символы, недопустимые в имени файла на одной из поддерживаемых
 * платформ: `/` и `\` - разделители пути (POSIX/Windows), `:` - зарезер-
 * вирован в NTFS, остальные запрещены FAT/NTFS и ломают скачивание в
 * Safari. Заменяем на `_`, чтобы имя оставалось узнаваемым и не слипалось
 * в случае нескольких запрещённых символов подряд.
 */
const FORBIDDEN_FILENAME_CHARS = /[\\/:*?"<>|]/g;

/**
 * Приводит значение к «безопасной» строке CSV.
 *
 * null и undefined дают пустую ячейку. Всё остальное приводится к строке
 * и проверяется на «формульный» префикс - включая числа, потому что
 * отрицательное число (`-3`) Excel тоже может трактовать как начало
 * формулы.
 */
export function escapeCsvCell(value) {
  let s = value === null || value === undefined ? "" : String(value);
  if (FORMULA_PREFIX.test(s)) {
    s = "'" + s;
  }
  return '"' + s.replace(/"/g, '""') + '"';
}

/**
 * Собирает CSV-строку из массива массивов.
 * Первая строка массива - заголовки (обрабатывается как обычная строка,
 * никаких специальных правил для неё нет).
 */
export function buildCsv(rows, separator = ";") {
  const body = rows
    .map((row) => row.map(escapeCsvCell).join(separator))
    .join("\r\n");
  return "\uFEFF" + body;
}

/**
 * Скачивает CSV-файл. Единственный способ инициировать выгрузку
 * в проекте - чтобы поведение было одинаковым везде.
 *
 * @param {string} filename - имя файла; ".csv" дописывается, если нет.
 *   Недопустимые символы заменяются на `_`.
 * @param {Array<Array<any>>} rows - массив строк, первая - заголовки
 * @param {string} [separator=';']
 */
export function downloadCsv(filename, rows, separator = ";") {
  const csv = buildCsv(rows, separator);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = String(filename || "").replace(
    FORBIDDEN_FILENAME_CHARS,
    "_",
  );
  a.href = url;
  a.download = safeName.endsWith(".csv") ? safeName : safeName + ".csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Даём браузеру время начать загрузку до освобождения URL. 500 мс -
  // эмпирически безопасное окно; больше не нужно, меньше - Safari
  // иногда не успевает.
  setTimeout(() => URL.revokeObjectURL(url), 500);
}
